import API from './api';

/**
 * Analytics Service for tracking user interactions
 * All methods return a Promise that resolves when the event is successfully tracked
 * or rejects when there's an error (but errors are caught and logged)
 */
class AnalyticsService {
  // Keep track of errors that have already been reported in this session
  // to prevent duplicate tracking
  _trackedErrors = new Set();
  
  // Get the base URL for API requests (without the /api suffix since analytics endpoints don't use it)
  _getBaseURL = () => {
    // Extract base URL from API.defaults.baseURL by removing the /api part
    const apiBaseURL = API.defaults.baseURL || '';
    return apiBaseURL.replace(/\/api$/, '');
  }
  
  // Authentication events
  trackLogin = () => this._trackEvent('login');
  trackLoginFailed = () => this._trackEvent('login_failed');
  trackLogout = () => this._trackEvent('logout');
  trackProfileLinked = () => this._trackEvent('profile_linked');
  
  // Profile events
  trackSummaryEdited = () => this._trackEvent('summary_edited');
  trackSummaryRegenerated = () => this._trackEvent('summary_regenerated');
  trackInterestAdded = (interest) => this._trackEvent('interest_added', { interest });
  trackInterestRemoved = (interest) => this._trackEvent('interest_removed', { interest });
  trackAbstractEdited = (abstractId) => this._trackEvent('abstract_edited', { abstractId });
  
  // Grant discovery events
  trackGrantViewed = (grantId, grant) => {
    const metadata = { grantId }; // Keep for backward compatibility
    
    // If we have the full grant object, extract the opportunity_id from it
    if (grant && grant.opportunity_id) {
      metadata.opportunity_id = grant.opportunity_id;
    }
    
    return this._trackEvent('grant_viewed', metadata);
  };
  trackGrantSearched = (query) => this._trackEvent('grant_searched', { query });
  trackGrantFiltered = (filters) => this._trackEvent('grant_filtered', { filters });
  trackGrantLinkClicked = (grantId, url, opportunityId) => this._trackEvent('external_grant_link_clicked', { 
    grantId, 
    url, 
    opportunity_id: opportunityId 
  });
  trackPaginationUsed = (page, context) => this._trackEvent('pagination_used', { page, context });
  trackRelatedFacultyViewed = (facultyId, grantId) => this._trackEvent('related_faculty_viewed', { facultyId, grantId });
  
  // Chat events
  trackChatStarted = (threadId) => this._trackEvent('chat_started', { threadId });
  trackChatQuery = (query, threadId) => this._trackEvent('chat_query', { query, threadId });
  trackChatEnded = (threadId) => this._trackEvent('chat_ended', { threadId });
  
  // Navigation events
  trackPageView = (page) => this._trackEvent('page_view', { page });
  
  // Helper to get CSRF token
  _getCSRFToken = () => {
    const csrfCookie = document.cookie
      .split(';')
      .map(c => c.trim())
      .find(c => c.startsWith('csrftoken='));
      
    if (csrfCookie) {
      return csrfCookie.split('=')[1];
    }
    return null;
  }

  // Tab tracking events
  trackTabStarted = (tabName) => {
    // Check if we have a valid tab name
    if (!tabName) {
      return Promise.resolve({ startTime: new Date() });
    }
    
    // Use a direct XHR for tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      // Get base URL (without /api suffix)
      const baseURL = this._getBaseURL();
      xhr.open('POST', `${baseURL}/analytics/track-event/`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true; // Always send credentials
      
      // Add CSRF token if available
      const csrfToken = this._getCSRFToken();
      if (csrfToken) {
        xhr.setRequestHeader('X-CSRFToken', csrfToken);
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            const tabViewId = response?.tab_view_id;
            resolve({ tabViewId, startTime: new Date() });
          } catch (e) {
            resolve({ startTime: new Date() });
          }
        } else if (xhr.status === 401 || xhr.status === 403) {
          // Authentication issue - silently handle it
          resolve({ startTime: new Date() });
        } else {
          resolve({ startTime: new Date() });
        }
      };
      
      xhr.onerror = function() {
        resolve({ startTime: new Date() });
      };
      
      const data = JSON.stringify({
        event_type: 'tab_view_started',
        page: '/tabs/' + tabName,
        metadata: { tab: tabName }
      });
      
      xhr.send(data);
    });
  };
  
  // System events
  trackError = (errorMsg, context) => {
    // Create a unique key for this error to prevent duplicates
    const path = context?.path || window.location.pathname;
    const errorType = context?.errorType || 'UNKNOWN';
    const errorKey = `${errorType}:${path}:${errorMsg}`;
    
    // Check if we've already tracked this exact error
    if (this._trackedErrors.has(errorKey)) {
      return Promise.resolve({ skipped: true });
    }
    
    // Add to tracked errors
    this._trackedErrors.add(errorKey);
    
    // Proceed with tracking
    return this._trackEvent('error', { errorMsg, context });
  };
  
  // Feedback events
  trackFeedbackSubmitted = (feedbackType) => this._trackEvent('feedback_submitted', { feedbackType });
  trackBugReported = (description) => this._trackEvent('bug_reported', { description });
  
  // Tab duration tracking
  trackTabEndedSync = (tabViewId, tabName, duration) => {
    try {
      // Don't send if we don't have valid params
      if (!tabName) {
        return Promise.reject(new Error('Missing tabName'));
      }
      
      // Ensure minimum duration is 1 second to avoid tracking issues
      if (!duration || duration < 1) {
        duration = 1; // Set minimum duration to 1 second
      }
      
      // Use a consistent path for tab tracking instead of the current URL
      const page = '/tabs/' + tabName;
      
      // Ensure we have a numeric duration
      const durationSeconds = parseInt(duration, 10) || 1;
      
      // Prepare the metadata for the event
      const metadata = { 
        tab: tabName,
        duration_seconds: durationSeconds
      };
      
      // Add tab_view_id if we have it (essential for updating existing records)
      if (tabViewId) {
        metadata.tab_view_id = tabViewId;
      }
      
      // First, try to use the direct API endpoint if we have a tabViewId
      if (tabViewId) {
        // Make a direct API call to end the tab view
        
        // Use XHR with credentials
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          const baseURL = this._getBaseURL();
          xhr.open('POST', `${baseURL}/analytics/api/tab-view/${tabViewId}/end/`, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.withCredentials = true; // Always send credentials
          
          // Add CSRF token if available
          const csrfToken = this._getCSRFToken();
          if (csrfToken) {
            xhr.setRequestHeader('X-CSRFToken', csrfToken);
          }
          
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({ success: true });
            } else if (xhr.status === 401 || xhr.status === 403) {
              // Authentication issue - silently handle it
              resolve({ success: false, reason: 'auth' });
            } else {
              resolve({ success: false });
            }
          };
          
          xhr.onerror = function() {
            resolve({ success: false });
          };
          
          const data = JSON.stringify({
            duration_seconds: durationSeconds
          });
          
          xhr.send(data);
        }).then(result => {
          if (result.success) {
            return result;
          }
          
          if (result.reason === 'auth') {
            // Don't try the fallback if it's an auth issue
            return result;
          }
          
          // If direct API failed, continue with regular tracking
          return this._sendTabEndedEvent(tabName, tabViewId, durationSeconds, page, metadata);
        });
      }
      
      // If no tabViewId, use regular tracking
      return this._sendTabEndedEvent(tabName, tabViewId, durationSeconds, page, metadata);
    } catch (err) {
      return Promise.reject(err);
    }
  };
  
  // Helper method to send tab_view_ended event
  _sendTabEndedEvent = (tabName, tabViewId, durationSeconds, page, metadata) => {
    // Use a direct XHR for tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const baseURL = this._getBaseURL();
      xhr.open('POST', `${baseURL}/analytics/track-event/`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true; // Always send credentials
      
      // Add CSRF token if available
      const csrfToken = this._getCSRFToken();
      if (csrfToken) {
        xhr.setRequestHeader('X-CSRFToken', csrfToken);
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ success: true });
        } else if (xhr.status === 401 || xhr.status === 403) {
          // Authentication issue - silently handle it
          resolve({ success: false, reason: 'auth' });
        } else {
          resolve({ success: false });
        }
      };
      
      xhr.onerror = function() {
        resolve({ success: false });
      };
      
      const data = JSON.stringify({
        event_type: 'tab_view_ended',
        page,
        metadata
      });
      
      xhr.send(data);
    });
  };
  
  /**
   * Base tracking method that sends events to the backend
   * @param {string} eventType - Type of event from the EVENT_TYPES in Django model
   * @param {object} metadata - Additional data to store with the event
   * @returns {Promise} - Promise that resolves when the event is tracked
   */
  _trackEvent = (eventType, metadata = {}) => {
    // Get current page path from window.location
    let page = window.location.pathname;
    
    // Skip analytics calls if we're on login page or not authenticated
    if (page === '/login' || !localStorage.getItem('access')) {
      return Promise.resolve({ skipped: true, reason: 'not_authenticated' });
    }
    
    // Replace '/home' with a more specific path for tab-related events
    if (page === '/home' && (
      eventType.includes('tab_') || 
      metadata.tab || 
      (metadata.feature && metadata.feature.includes('tab'))
    )) {
      const tabName = metadata.tab || 'unknown';
      page = '/tabs/' + tabName;
    }
    
    // For tab paths, ensure we always have an event_type
    if (page.startsWith('/tabs/') && !eventType) {
      return Promise.reject(new Error('Missing event_type for tab path'));
    }
    
    // Get base URL (without /api suffix since analytics endpoints don't use the /api prefix)
    const baseURL = this._getBaseURL();
    
    // Get the current time (to limit number of requests)
    const now = new Date().getTime();
    
    // Use a debounce mechanism for analytics tracking
    // Skip if we've tracked too recently (prevent spamming of analytical endpoints)
    const lastTracked = parseInt(localStorage.getItem('lastAnalyticsTracked') || '0', 10);
    if (now - lastTracked < 300) { // 300ms minimum between tracking calls
      return Promise.resolve({ skipped: true, reason: 'rate_limited' });
    }
    
    // Update last tracked time
    localStorage.setItem('lastAnalyticsTracked', now.toString());
    
    return API.post(`${baseURL}/analytics/track-event/`, {
      event_type: eventType,
      page,
      metadata
    }, {
      withCredentials: true // Ensure cookies are sent with the request
    }).catch(error => {
      // Check for authentication errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // User is not authenticated - silently handle
        return { skipped: true, reason: 'auth' };
      }
      
      // Check for rate limiting
      if (error.response && error.response.status === 429) {
        // Rate limited - silently handle
        return { skipped: true, reason: 'rate_limited' };
      }
      
      // Silent fail for all other errors - don't disrupt user experience
      return null; // Return null instead of rejecting to avoid breaking the app flow
    });
  }
}

// Export a singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService; 